import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { type MouseEvent, useEffect, useRef, useState } from 'react';
import { IoClose, IoMenu } from 'react-icons/io5';

import ActiveLink from '@/common/active-link';
import Button from '@/common/button';
import { LazySignInModal, prefetchSignInModal } from '@/components/modals/lazy-sign-in-modal';
import { LazySignUpModal } from '@/components/modals/lazy-sign-up-modal';
import { useFeatureToggles } from '@/contexts/feature-toggle-context';
import { prefetchTrialSignUp, useTrialSignUp } from '@/contexts/trial-sign-up-context';
import { useAuth } from '@/hooks/use-auth';
import { usePulseRingScale } from '@/hooks/use-pulse-ring-scale';
import { getBlogUrl } from '@/utils/get-blog-url';
import { HOME_CTA_ASSINE_HREF, type HomeCtaSlot, homeCtaSlot } from '@/utils/home-cta-cookie';

import LanguageSwitcher from './language-switcher';

// Both auth modals keep their forms out of the first-load bundle of every page with a header — static,
// they dragged @headlessui, yup, react-hook-form, @hookform/resolvers and both captcha providers along —
// and both pair their chunk with a skeleton, so the click never lands on an empty screen.
//
// SignInModal used to be the exception, because it owned the header's Login button and deferring the button
// would have cost layout stability above the fold. The button is plain markup, so it now lives here in the
// header instead (still server-rendered, same classes, same label) and only the dialog behind it is lazy.
// The two panels below sit outside the mobile-menu gate on purpose: desktop and mobile open the same one.

type HeaderNavItem = {
  href: string;
  label: string;
  className?: string;
  checkBlogActive?: boolean;
  locale?: false;
  prefetch?: boolean;
};

/** CTA principal no lugar do "Teste grátis" (abre o cadastro): um link com rótulo próprio, como o "Assine agora" das versões assine da Home. */
export interface HeaderPrimaryCta {
  label: string;
  href: string;
}

/**
 * `primaryCta`, o CTA principal (SCRUM-2631):
 * - um objeto: a página decide e manda esse CTA (a Home nas versões "Assine agora");
 * - `null`: a página decide e oferece o teste grátis (a Home nas outras versões);
 * - ausente: segue o cookie da versão sorteada na Home (src/utils/home-cta-cookie.ts). Os dois CTAs saem no HTML e o
 *   CSS inline do `_document` mostra só o do grupo do visitante, inclusive nas páginas estáticas.
 */
export type HeaderPrimaryCtaChoice = HeaderPrimaryCta | null | undefined;

export default function Header({ variant, hideAuthActions = false, primaryCta }: { variant?: 'primary' | 'secondary' | 'checkout'; hideAuthActions?: boolean; primaryCta?: HeaderPrimaryCtaChoice }) {
  const auth = useAuth();
  const { user } = auth;

  const { t } = useTranslation('common');

  const router = useRouter();

  const { christmas: isEnableChristmasActive } = useFeatureToggles();
  const { openTrialSignUp } = useTrialSignUp();

  const [menuOpen, setMenuOpen] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  // The trial CTA pulse starts its ring from the button's own size (global.css, trialCtaPulseRing).
  usePulseRingScale(menuOpen);
  const [isScrolled, setIsScrolled] = useState(false);
  const menuToggleRef = useRef<HTMLButtonElement>(null);
  const menuCloseRef = useRef<HTMLButtonElement>(null);

  const affiliatePage = router.pathname === '/affiliates';

  const urlLogo = isEnableChristmasActive ? '/assets/images/logo-np-natal.webp' : '/assets/images/novo-logo-verde-branco.png';

  // prefetch=false: the desktop nav is always in view, so every page downloaded the chunk of every
  // destination (pricing 177KiB, games 42KiB, affiliates 28KiB wasted on the home). Hover still prefetches.
  // The blog link was the one left out: PSI caught it as an 87KB High-priority fetch of /blog on every home load.
  const navItems: HeaderNavItem[] = [
    { href: '/games', label: t('header.games'), prefetch: false },
    { href: '/pricing', label: t('header.pricing'), className: 'header-nav-plus', prefetch: false },
    { href: '/download', label: t('header.download'), prefetch: false },
    { href: '/affiliates', label: t('header.affiliates'), prefetch: false },
    { href: '/support', label: t('header.support'), prefetch: false },
    // The blog item was the only one of the six left prefetching, and it only leaked on the default
    // locale: with `locale={false}` a localized href like `/pt/blog` does not resolve to a route, so the
    // prefetcher skipped it, while in `en` the href is `/blog` and every page pulled `pages/blog-*.js`
    // (25.975 B, 94 % unused). Hover still prefetches.
    {
      href: getBlogUrl(router.locale),
      label: t('footer_nav.blog.title'),
      checkBlogActive: true,
      locale: false,
      prefetch: false,
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const nextIsScrolled = window.scrollY > 24;

      setIsScrolled((currentIsScrolled) => (currentIsScrolled === nextIsScrolled ? currentIsScrolled : nextIsScrolled));
    };

    handleScroll();

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  useEffect(() => {
    if (menuOpen) {
      menuCloseRef.current?.focus();

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          setMenuOpen(false);
        }
      };

      window.addEventListener('keydown', handleKeyDown);

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }

    menuToggleRef.current?.focus();
    return undefined;
  }, [menuOpen]);

  const handleShowLogin = () => {
    if (user) {
      if (affiliatePage) {
        router.push('/dashboard/affiliate');
      } else {
        router.push('/dashboard/overview');
      }

      return;
    }

    setShowSignIn(true);
  };

  const handleCloseSignUp = () => {
    setShowSignUp(false);
    setShowSignIn(true);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const handleOpenTrialFromMenu = () => {
    closeMenu();
    openTrialSignUp('header');
  };

  // On the home itself the logo must not reach next/link: a client-side navigation to the URL already open
  // re-fetches its getServerSideProps data, and when that fetch fails (Cloudflare answers it with a 403
  // challenge for visitors it flags) the router throws "Invariant: attempted to hard navigate to the same URL"
  // instead of reloading.
  const handleLogoClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (router.asPath !== '/') return;

    event.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleMenuLogoClick = (event: MouseEvent<HTMLAnchorElement>) => {
    closeMenu();
    handleLogoClick(event);
  };

  const pulseClassName = `font-bold tracking-widest transition-all duration-300 ${isScrolled ? 'pulse-circle-green' : 'pulse-circle-white'}`;

  // `inMenu`: o CTA do menu mobile, que fecha o menu ao ser usado.
  const renderLinkCta = (cta: HeaderPrimaryCta, inMenu: boolean, slot?: HomeCtaSlot) => (
    <Button as='link' href={cta.href} prefetch={false} onClick={inMenu ? closeMenu : undefined} variant='white' size='sm' data-pulse-ring='' className={pulseClassName} {...(slot ? homeCtaSlot(slot) : {})}>
      {cta.label}
    </Button>
  );

  const renderTrialCta = (inMenu: boolean, slot?: HomeCtaSlot) => (
    <Button onClick={inMenu ? handleOpenTrialFromMenu : () => openTrialSignUp('header')} aria-haspopup='dialog' onPointerEnter={prefetchTrialSignUp} onFocus={prefetchTrialSignUp} variant='white' size='sm' data-pulse-ring='' className={pulseClassName} {...(slot ? homeCtaSlot(slot) : {})}>
      {t('header.button_free_trial')}
    </Button>
  );

  const renderPrimaryCta = (inMenu: boolean) => {
    if (primaryCta) return renderLinkCta(primaryCta, inMenu);
    if (primaryCta === null) return renderTrialCta(inMenu);
    return (
      <>
        {renderTrialCta(inMenu, 'teste')}
        {renderLinkCta({ label: t('header.button_subscribe_now'), href: HOME_CTA_ASSINE_HREF }, inMenu, 'assine')}
      </>
    );
  };

  return (
    <>
      <header className='site-header sticky top-0 z-50 m-0 flex w-full items-center justify-center border-b border-white/10 bg-[linear-gradient(180deg,rgba(6,8,10,0.92),rgba(6,8,10,0.70))] backdrop-blur-[14px] transition-all duration-300'>
        <div className={`mx-auto flex w-full max-w-[1400px] items-center justify-between gap-5 px-5 transition-all ${isScrolled ? 'py-3' : 'py-5'}`}>
          <div className='hidden shrink-0 items-center gap-6 min-[320px]:flex'>
            {/* prefetch=false: the logo is on every page of the site, so without it every route
                downloads the home bundle (pages/index + its shared chunks) inside the LCP window —
                6.7 KB twice at ~1.3 s on /technology mobile, where the paint is at 1.33 s. The logo is
                return navigation, not a hot path; the click still fetches the route. Same argument as
                the nav items above (`navItems`). */}
            <Link href='/' prefetch={false} onClick={handleLogoClick} className='inline-flex items-center transition-opacity hover:opacity-80'>
              {/* `sizes` is what CSS actually renders, not the `width` above: without it next/image builds the
                  srcset from `width` alone and a DPR-1.75 phone downloads the w=384 variant (8.7 KB) to paint a
                  100 px logo. It is a High-priority request leaving the gate alongside the render-blocking CSS
                  and the fonts, which is the window Lighthouse charges against LCP. With `sizes` the browser
                  picks w=256 and the same pixels cost ~4 KB. `priority` stays: the logo is above the fold on
                  every page and it is 4 KB, not the thing starving the paint. */}
              <Image width={180} height={40} sizes='(min-width: 1024px) 180px, 100px' src={urlLogo} alt='NoPing Logo' priority className='h-auto w-[100px] lg:w-[180px]' />
            </Link>
          </div>

          {/* Links do menu Desktop */}
          {variant === 'primary' && (
            <nav className='hidden items-center gap-[30px] xl:flex'>
              {navItems.map((item) => (
                <ActiveLink key={item.href} activeClassName='text-white' href={item.href} locale={item.locale} prefetch={item.prefetch} checkBlogActive={item.checkBlogActive} className={`${item.className ?? ''} relative text-[13px] font-semibold uppercase tracking-[0.08em] text-slate-300 transition-colors duration-200 hover:text-[#9CFF2E]`.trim()}>
                  {item.label}
                </ActiveLink>
              ))}
            </nav>
          )}

          {variant === 'checkout' && (
            <div className='flex items-center gap-4'>
              <LanguageSwitcher />
            </div>
          )}

          {/* Desktop: idioma, login, teste; Mobile: teste e botão de menu */}
          {variant === 'primary' && (
            <div className='flex items-center gap-4 lg:gap-8'>
              <div className='hidden items-center gap-8 xl:flex'>
                <LanguageSwitcher />

                {!hideAuthActions && (
                  <div className='flex items-center'>
                    {/* Classes and label copied from the button SignInModal used to render internally, so
                        the above-the-fold markup is byte-for-byte what it was. Both warm-up gestures are
                        needed: hover never fires for keyboard users, focus never fires for mouse users. */}
                    <button type='button' onClick={handleShowLogin} onPointerEnter={prefetchSignInModal} onFocus={prefetchSignInModal} className='text-center text-sm font-bold uppercase leading-[30.80px] text-neutral-50 transition-colors duration-200 hover:text-[#9CFF2E]'>
                      {user?.name ? t('header.my_account') : t('header.button_login')}
                    </button>
                  </div>
                )}
              </div>

              {/* A prefetched link to /trial here pulls the whole /trial bundle (custom trial LP registry) onto every page with a header. */}
              {!hideAuthActions && renderPrimaryCta(false)}

              <button ref={menuToggleRef} type='button' onClick={() => setMenuOpen(!menuOpen)} className='inline-flex h-9 w-full min-w-9 max-w-9 items-center justify-center rounded-lg border border-white/10 text-white hover:bg-white/[0.04] xl:hidden' aria-label={menuOpen ? t('header.menu_close') : t('header.menu_open')} aria-expanded={menuOpen} aria-controls='mobile-menu'>
                {menuOpen ? <IoClose size={24} /> : <IoMenu size={22} />}
              </button>
            </div>
          )}

          {/* Secondary: idioma e voltar ao site, alinhados à direita */}
          {variant === 'secondary' && (
            <div className='ms-auto flex items-center gap-6'>
              <div className='hidden lg:flex'>
                <LanguageSwitcher />
              </div>

              <Link href='/' prefetch={false} className='text-nowrap border-b border-transparent text-center text-[13px] font-semibold uppercase tracking-[0.08em] text-slate-300 transition-colors duration-200 hover:text-[#9CFF2E]'>
                {t('header.back_to_site')}
              </Link>
            </div>
          )}
        </div>
      </header>

      {/* Menu Mobile */}
      {menuOpen && (
        <>
          <div className='fixed inset-0 z-40 bg-[rgba(5,7,10,0.66)] backdrop-blur-md' onClick={closeMenu} />

          {/* The drawer mirrors with `end-0`/`border-s`; the drop shadow has to be flipped by hand because a shadow x offset cannot be logical. */}
          <aside id='mobile-menu' data-np-modal-surface='' className='fixed inset-y-0 end-0 z-50 flex w-[min(360px,85vw)] flex-col overflow-y-auto border-s border-white/[0.08] bg-[linear-gradient(180deg,#0f1218_0%,#0a0b0e_100%)] p-5 shadow-[-12px_0_40px_rgba(0,0,0,0.5)] rtl:shadow-[12px_0_40px_rgba(0,0,0,0.5)]'>
            <div className='flex items-center justify-between border-b border-white/[0.06] pb-[18px]'>
              <Link href='/' prefetch={false} onClick={handleMenuLogoClick}>
                <Image width={100} height={28} src={urlLogo} alt='NoPing Logo' className='h-auto w-[100px]' />
              </Link>

              <button ref={menuCloseRef} type='button' aria-label={t('header.menu_close')} className='inline-flex size-9 items-center justify-center rounded-lg border border-white/10 text-white hover:bg-white/[0.04]' onClick={closeMenu}>
                <IoClose size={22} />
              </button>
            </div>

            <nav className='flex flex-col gap-5 border-b border-white/[0.06] py-[18px]'>
              {!hideAuthActions && (
                <button className='rounded-lg text-start text-[17px] font-medium text-white transition-colors' onClick={() => handleShowLogin()}>
                  {user?.name ? t('header.my_account') : t('header.button_login')}
                </button>
              )}

              {navItems.map((item) => (
                <Link key={item.href} href={item.href} locale={item.locale} prefetch={item.prefetch} onClick={closeMenu} className='rounded-lg text-[17px] font-medium text-white transition-colors hover:text-[#9CFF2E]'>
                  {item.label}
                </Link>
              ))}
            </nav>

            <div className='mt-5 flex flex-col gap-5'>
              {!hideAuthActions && renderPrimaryCta(true)}
              <LanguageSwitcher mode='list' />
            </div>
          </aside>
        </>
      )}

      {/* Outside the `menuOpen` gate above: the desktop Login button opens this same panel, and closing the
          mobile menu must not tear down an open dialog. `affiliatePage` now carries its real value — it was
          hardcoded true here, which sent a logged-in user returning from sign-up to the affiliate dashboard
          from any page. `handleShowLogin` already short-circuits that case before the panel ever opens. */}
      {showSignIn && <LazySignInModal affiliatePage={affiliatePage} classNameButton='hidden' forceOpenModal onModalStateChange={(state) => setShowSignIn(state)} onModalSignUpStateChange={(state) => setShowSignUp(state)} />}

      {showSignUp && <LazySignUpModal closeModalSingUp={handleCloseSignUp} />}
    </>
  );
}
